/**
 * 消息内容解析模块
 * 解析自定义标签、提取样式、安全过滤
 */

import { marked } from 'marked'

import { preprocessContent } from './preprocessContent'
import { sanitizeHtml } from './sanitizeHtml'

// 自定义标签列表
const CUSTOM_TAGS = ['message', 'narration', 'html-box']

// 自闭合标签
const SELF_CLOSING_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
])

export interface ParsedSegment {
  type: 'message' | 'narration' | 'html-box' | 'text' | 'html'
  content: string
}

export interface ParsedMessage {
  segments: ParsedSegment[]
  styles: string[] // 提取的 <style> 内容
}

/**
 * 解析消息内容
 * @param content 原始消息内容
 * @param characterName 角色名（用于占位符替换）
 * @param userName 用户名（用于占位符替换）
 * @returns 解析后的消息结构
 */
export function parseMessageContent(
  content: string,
  characterName?: string,
  userName?: string
): ParsedMessage {
  if (!content) return { segments: [], styles: [] }

  // 1. 预处理：占位符替换
  const processed = preprocessContent(content, characterName, userName)

  // 2. 保护 <html-box> 内容（用占位符替换，避免其内部 style/script 被误处理）
  const htmlBoxBlocks: string[] = []
  const protectedContent = processed.replace(
    /<html-box[^>]*>[\s\S]*?<\/html-box>/gi,
    (match) => {
      const index = htmlBoxBlocks.length
      htmlBoxBlocks.push(match)
      return `__HTML_BOX_PLACEHOLDER_${index}__`
    }
  )

  // 3. 对非 html-box 内容：提取 <style> 标签
  const styles: string[] = []
  let withoutStyles = protectedContent.replace(
    /<style[^>]*>([\s\S]*?)<\/style>/gi,
    (_, styleContent) => {
      styles.push(styleContent)
      return ''
    }
  )

  // 4. 移除 <script> 标签（安全考虑，只对非 html-box 内容）
  withoutStyles = withoutStyles.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')

  // 5. 安全过滤（只对非 html-box 内容）
  // 注意：必须确保 html-box 占位符不被影响
  const sanitized = sanitizeHtml(
    withoutStyles.replace(
      /__HTML_BOX_PLACEHOLDER_(\d+)__/g,
      (_, index) => `__HTML_BOX_PLACEHOLDER_SAFE_${index}__`
    )
  ).replace(
    /__HTML_BOX_PLACEHOLDER_SAFE_(\d+)__/g,
    (_, index) => `__HTML_BOX_PLACEHOLDER_${index}__`
  )

  // 6. 还原 html-box 占位符（内容保持原样）
  const restored = sanitized.replace(
    /__HTML_BOX_PLACEHOLDER_(\d+)__/g,
    (_, index) => htmlBoxBlocks[parseInt(index, 10)]
  )

  // 7. 解析自定义标签
  const segments = parseWithStack(restored.trim())

  return { segments, styles }
}

/**
 * 使用栈解析 HTML，正确处理嵌套标签
 */
function parseWithStack(content: string): ParsedSegment[] {
  if (!content) return []

  const segments: ParsedSegment[] = []
  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*\/?>/g

  let lastIndex = 0
  let match
  const stack: Array<{ tag: string; startIndex: number }> = []
  let blockStartIndex = -1
  let blockRootTag = ''

  while ((match = tagPattern.exec(content)) !== null) {
    const fullMatch = match[0]
    const tagName = match[1].toLowerCase()
    const isClosingTag = fullMatch.startsWith('</')
    const isSelfClosing =
      fullMatch.endsWith('/>') || SELF_CLOSING_TAGS.has(tagName)

    if (stack.length === 0) {
      // 栈为空，检测新的 HTML 块开始
      if (!isClosingTag) {
        // 先处理之前的纯文本
        if (match.index > lastIndex) {
          const text = content.slice(lastIndex, match.index).trim()
          if (text) {
            segments.push({
              type: 'text',
              content: (marked.parse(text) as string).trim(),
            })
          }
        }

        if (isSelfClosing) {
          // 自闭合标签
          segments.push({ type: 'html', content: fullMatch })
          lastIndex = match.index + fullMatch.length
        } else {
          // 开始标签，入栈
          stack.push({ tag: tagName, startIndex: match.index })
          blockStartIndex = match.index
          blockRootTag = tagName
        }
      }
      // 如果是闭合标签但栈为空，忽略（格式错误的 HTML）
    } else {
      // 栈不为空，追踪嵌套
      if (isClosingTag) {
        // 特殊处理自定义标签：如果是自定义标签的结束标签，强制闭合所有嵌套标签
        if (CUSTOM_TAGS.includes(blockRootTag) && tagName === blockRootTag) {
          stack.length = 0
          const blockEnd = match.index + fullMatch.length
          const blockContent = content.slice(blockStartIndex, blockEnd)
          const innerContent = extractInnerContent(blockContent, blockRootTag)
          segments.push({
            type: blockRootTag as 'message' | 'narration' | 'html-box',
            content: innerContent,
          })
          lastIndex = blockEnd
          blockStartIndex = -1
          blockRootTag = ''
        } else if (
          stack.length > 0 &&
          stack[stack.length - 1].tag === tagName
        ) {
          // 普通标签匹配
          stack.pop()
          if (stack.length === 0) {
            const blockEnd = match.index + fullMatch.length
            const blockContent = content.slice(blockStartIndex, blockEnd)
            if (CUSTOM_TAGS.includes(blockRootTag)) {
              const innerContent = extractInnerContent(
                blockContent,
                blockRootTag
              )
              segments.push({
                type: blockRootTag as 'message' | 'narration' | 'html-box',
                content: innerContent,
              })
            } else {
              segments.push({ type: 'html', content: blockContent })
            }
            lastIndex = blockEnd
            blockStartIndex = -1
            blockRootTag = ''
          }
        }
      } else if (!isSelfClosing) {
        // 嵌套的开始标签，入栈
        stack.push({ tag: tagName, startIndex: match.index })
      }
    }
  }

  // 处理剩余文本（仅在没有未闭合标签时）
  if (stack.length === 0 && lastIndex < content.length) {
    const text = content.slice(lastIndex).trim()
    if (text) {
      segments.push({
        type: 'text',
        content: (marked.parse(text) as string).trim(),
      })
    }
  }

  // 处理未闭合的标签
  if (blockStartIndex !== -1) {
    const blockContent = content.slice(blockStartIndex)
    if (blockRootTag === 'html-box') {
      // 不完整的 html-box 不渲染，避免破碎的 iframe
    } else if (CUSTOM_TAGS.includes(blockRootTag)) {
      const innerContent = extractInnerContent(blockContent, blockRootTag)
      segments.push({
        type: blockRootTag as 'message' | 'narration' | 'html-box',
        content: innerContent,
      })
    } else {
      segments.push({ type: 'html', content: blockContent })
    }
  }

  return segments
}

/**
 * 提取标签内部内容
 */
function extractInnerContent(blockContent: string, tagName: string): string {
  // 匹配开始标签（包括属性）
  const startTagPattern = new RegExp(`^<${tagName}[^>]*>`, 'i')
  // 匹配结束标签
  const endTagPattern = new RegExp(`</${tagName}>$`, 'i')

  return blockContent
    .replace(startTagPattern, '')
    .replace(endTagPattern, '')
    .trim()
}

/**
 * 为样式添加作用域前缀
 * @param styles 原始样式数组
 * @param msgId 消息 ID
 * @returns 添加前缀后的样式字符串
 */
export function scopeStyles(styles: string[], msgId: string): string {
  if (styles.length === 0) return ''

  return styles.join('\n')
}
